<template>
  <div class="buy-go-component">
    <div class="row mb-3">
      <div class="col-lg-6">
        <AiChatCard
          :companyId="props.companyId"
          :endpoint="`/api/company/${props.companyId}/portfolio/buy-chat`"
          :greeting="$t('ai_chat.greetings.buy')"
          @analysis-complete="handleFileAnalysisComplete"
        />
      </div>
      <div class="col-lg-6">
        <SourcingPreferenceCard
          v-model="globalSettings"
          @user-select="userSelectedSourcingMode = true"
        />
      </div>
    </div>

    <CustomRequirementCard
      v-if="isOtherMode"
      v-model="globalSettings.custom_requirement"
    />

    <div class="card mt-3">
      <div class="card-header">
        <h5 class="mb-1">{{ $t("go_buy.price_breakdown_title") }}</h5>
        <p class="text-muted mb-0">
          {{ $t("go_buy.price_breakdown_description") }}
        </p>
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-end border-bottom pb-4 mb-4">
          <div class="col-md-4 col-lg-3">
            <label class="form-label">
              {{ $t("go_buy.form.volume.label") }}
            </label>
            <input
              v-model.number="volume"
              type="number"
              min="1"
              class="form-control"
            />
          </div>
          <div class="col-md-4 col-lg-3">
            <label class="form-label">
              {{ $t("go_buy.certificate_scheme") }}
            </label>
            <select v-model="globalSettings.eac_source" class="form-select">
              <option
                v-for="source in formOptions?.eac_sources"
                :key="source.value"
                :value="source.value"
              >
                {{ source.label }}
              </option>
            </select>
          </div>
          <div class="col-md-auto">
            <button
              type="button"
              class="btn btn-outline-primary"
              :disabled="!canAskForQuote"
              @click="fetchPricing"
            >
              {{ $t("go_buy.ask_for_quote_button") }}
            </button>
          </div>
        </div>

        <LoadingIndicatorComponent
          v-if="pricingState.status === 'loading'"
          inline
          :message="pricingState.message"
        />

        <div
          v-else-if="pricingState.status === 'error'"
          class="alert alert-warning mb-0"
        >
          {{ pricingState.message }}
        </div>

        <div
          v-else-if="pricingState.status === 'agent_reach_out'"
          class="alert alert-warning mb-0"
        >
          {{ $t("go_buy.agent_quote_prompt") }}
        </div>

        <div v-else-if="pricingDetails" class="row g-4">
          <div class="col-12">
            <h6 class="mb-3">{{ $t("go_buy.purchase_summary") }}</h6>
            <div class="bg-light rounded-3 p-3">
              <div class="d-flex justify-content-between gap-3 flex-wrap">
                <span class="fw-semibold">
                  {{ $t("go_buy.volume_to_buy") }}
                </span>
                <span>{{ volume }} MWh</span>
              </div>
              <div class="d-flex justify-content-between gap-3 flex-wrap mt-2">
                <span class="fw-semibold">
                  {{ $t("company_orders.buy.total_price") }}
                </span>
                <span class="fw-semibold">
                  {{ formatEuro(pricingDetails.price) }}
                </span>
              </div>
              <div
                class="indicative-note text-secondary small mt-2 d-flex align-items-center gap-2"
              >
                <FontAwesomeIcon
                  :icon="['far', 'circle-info']"
                  class="flex-shrink-0"
                />
                {{ $t("bulk_buy.quotes_indicative_note") }}
              </div>
              <div
                v-if="quoteProvenance"
                class="quote-provenance text-secondary small mt-1 font-monospace"
              >
                {{ quoteProvenance }}
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-muted">
          {{ $t("bulk_buy.pricing_idle") }}
        </div>

        <div class="row g-3 align-items-end mt-1">
          <div class="col-lg">
            <label class="form-label">
              {{ $t("go_buy.form.operation_notes.label") }}
            </label>
            <textarea
              v-model="operationNotes"
              class="form-control"
              rows="2"
              :placeholder="$t('go_buy.form.operation_notes.placeholder')"
            />
          </div>
          <div class="col-lg-auto">
            <button
              type="button"
              class="btn btn-success"
              :disabled="!canSubmit"
              @click="submitBuyOrder"
            >
              {{ $t("go_buy.send_purchase_request_button") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatEuro } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Company } from "@/types";
import { computed, inject, ref, watch, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import type { GlobalPurchaseSettings } from "../bulk_buy/bulkBuyTypes";
import {
  CodRequirement,
  EACSource,
  Ecolabel,
  marketDataDateLabel,
  PurchaseMode,
  Support,
} from "../bulk_buy/bulkBuyTypes";
import { mergeAiGlobalSettings } from "../bulk_buy/bulkBuyAiMapping";
import CustomRequirementCard from "../bulk_buy/CustomRequirementCard.vue";
import SourcingPreferenceCard from "../bulk_buy/SourcingPreferenceCard.vue";
import { useBulkBuyOptions } from "../bulk_buy/useBulkBuyOptions";
import AiChatCard from "../common/AiChatCard.vue";
import { useBuyPricing } from "./useBuyPricing";

const props = defineProps<{
  companyId: number;
  company: Company;
}>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const { t } = useI18n();
const { options: formOptions } = useBulkBuyOptions();

const globalSettings = ref<GlobalPurchaseSettings>({
  purchase_mode: PurchaseMode.CHEAPEST,
  eac_source: EACSource.STANDARD_AIB,
  technology: "ANY",
  ecolabel: Ecolabel.NONE,
  support: Support.ANY,
  cod_requirement: CodRequirement.ANY,
  vintage: "",
  hourly_matching: false,
  monthly_matching: false,
});

const volume = ref(1000);
const operationNotes = ref("");
const uploadedFileId = ref<number | null>(null);
const analysisFormResponse = ref<Record<string, unknown> | null>(null);
const isSubmitting = ref(false);
const userSelectedSourcingMode = ref(false);

const prefillBuyVolume = inject<Ref<number | null>>(
  "prefillBuyVolume",
  ref(null)
);

watch(
  prefillBuyVolume,
  (newVolume) => {
    if (newVolume && newVolume > 0) {
      volume.value = newVolume;
      prefillBuyVolume.value = null;
    }
  },
  { immediate: true }
);

const destinationCountry = computed(() => props.company.country ?? "");
const { pricingState, fetchPricing, clearPricing } = useBuyPricing(
  volume,
  destinationCountry,
  globalSettings
);

const isOtherMode = computed(
  () => globalSettings.value.purchase_mode === PurchaseMode.OTHER
);

const canAskForQuote = computed(() => {
  // OTHER mode is quoted by hand from the free text, so the backend requires it.
  if (isOtherMode.value && !globalSettings.value.custom_requirement?.trim()) {
    return false;
  }
  return (
    volume.value > 0 &&
    !!destinationCountry.value &&
    pricingState.value.status !== "loading"
  );
});

const canSubmit = computed(() => {
  if (isSubmitting.value || volume.value < 1 || !destinationCountry.value) {
    return false;
  }

  return (
    pricingState.value.status === "success" ||
    pricingState.value.status === "agent_reach_out"
  );
});

const pricingDetails = computed(() => {
  return pricingState.value.status === "success" ? pricingState.value : null;
});

// "Market levels as of {date} · Ref {id}" — the quote's audit trail, shown under the total.
const quoteProvenance = computed(() => {
  const details = pricingDetails.value;
  if (!details) return "";
  const parts = [];
  if (details.price_date) {
    parts.push(
      t("bulk_buy.market_levels_as_of", {
        date: marketDataDateLabel(details.price_date),
      })
    );
  }
  if (details.pricing_request_id) {
    parts.push(
      t("bulk_buy.quote_reference", {
        id: details.pricing_request_id.slice(0, 8).toUpperCase(),
      })
    );
  }
  return parts.join(" · ");
});

// Human-readable summary of the AI's structured constraints, used as the custom requirement when
// an extracted ADVANCED request lands on the OTHER card (quoted by hand from free text).
const summarizeExtractedRequirements = (formData: any): string => {
  const constraintFields = [
    "technology",
    "ecolabel",
    "support",
    "vintage",
    "cod_requirement",
    "production_month",
  ] as const;
  const parts = constraintFields
    .filter((field) => formData?.[field])
    .map((field) => `${field.replaceAll("_", " ")}: ${formData[field]}`);
  // Country-specific sourcing is not in the scheme picker — carry it into the free text so the
  // agent quoting the request sees it.
  const source = formData?.eac_source;
  if (source && !(String(source).toUpperCase() in EACSource)) {
    parts.push(`source country: ${source}`);
  }
  return parts.join("; ");
};

const handleFileAnalysisComplete = (data: any) => {
  if (!data) {
    uploadedFileId.value = null;
    analysisFormResponse.value = null;
    clearPricing();
    return;
  }

  if (data.upload_id) {
    uploadedFileId.value = data.upload_id;
  }
  if (data.form_data) {
    analysisFormResponse.value = data.form_data;
  }

  // Enum-guarded merge shared with the buy & cancel page: maps an extracted ADVANCED to OTHER and
  // applies scheme values (incl. REGO/I-REC); extracted country codes are not schemes and flow
  // into the free-text requirement below instead.
  globalSettings.value = mergeAiGlobalSettings(
    globalSettings.value,
    data.global_settings,
    { preserveUserPurchaseMode: userSelectedSourcingMode.value }
  );
  // OTHER mode needs its free-text requirement; derive it from the document's own words or the
  // extracted constraints so the manual quote request is self-explanatory.
  if (
    globalSettings.value.purchase_mode === PurchaseMode.OTHER &&
    !globalSettings.value.custom_requirement?.trim()
  ) {
    const requirement =
      data.form_data?.operation_notes ||
      summarizeExtractedRequirements(data.form_data);
    if (requirement) {
      globalSettings.value = {
        ...globalSettings.value,
        custom_requirement: requirement,
      };
    }
  }

  if (data.form_data?.volume && data.form_data.volume > 0) {
    volume.value = Math.floor(data.form_data.volume);
  }
  if (data.form_data?.operation_notes) {
    operationNotes.value = data.form_data.operation_notes;
  }

  clearPricing();
};

const submitBuyOrder = async () => {
  if (!canSubmit.value) return;

  try {
    isSubmitting.value = true;

    const pricing =
      pricingState.value.status === "success" ? pricingState.value : null;

    const pricingRequestId =
      pricingState.value.status === "success" ||
      pricingState.value.status === "agent_reach_out"
        ? pricingState.value.pricing_request_id
        : undefined;

    const payload = {
      volume: volume.value,
      country: destinationCountry.value,
      operation_notes: operationNotes.value ?? undefined,
      uploaded_file_id: uploadedFileId.value ?? undefined,
      analysis_form_response: analysisFormResponse.value ?? undefined,
      ...globalSettings.value,
      quoted_total_price: pricing?.price,
      pricing_request_id: pricingRequestId,
    };

    await goSolidApi.post(
      `/api/company/${props.companyId}/portfolio/buy-order`,
      payload
    );

    globalSuccessHandler(
      t("go_buy.purchase_request_submitted"),
      t("go_buy.purchase_request_submitted_detail", { volume: volume.value })
    );
    clearPricing();
    operationNotes.value = "";
    uploadedFileId.value = null;
    analysisFormResponse.value = null;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>
